import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExamService } from '../../../Services/examServices';
import LiveExam from '../../../Services/liveexam.services';
import { errorResponseHelper } from '../../../helpers/request.helper';
import { defaultTheme, withAlpha, PageShell, TopBar, UserChip, Pill, NxButton, NxIcon } from '../../../components/nx';
import { AuthService } from '../../../Services/authServices';
import Auth from '../../../auth/auth';
import fingerPrint from '../../../helpers/fingerprintjs';
import AllPagesPDFViewer from '../Instructions/pdfview/AllPagesPdfViewer';

const t = defaultTheme;

export default function Dashboard() {
    const navigate = useNavigate();
    const [liveExams, setLiveExams] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [enrollingId, setEnrollingId] = useState<string | null>(null);
    const [startingId, setStartingId] = useState<string | null>(null);
    const [certificateLoading, setCertificateLoading] = useState<string | null>(null);
    const [profile, setProfile] = useState<any>();
    const [activeTab, setActiveTab] = useState(0);
    const [instructionModal, setInstructionModal] = useState<{ examId: string; pdf: string | null; loading: boolean } | null>(null);
    const [instructionAgreed, setInstructionAgreed] = useState(false);

    async function fetchExams() {
        try {
            const res = await ExamService.getLiveExamsWithEnrollStatus();
            if (res.status === 200) {
                setLiveExams(res.data.data.exams || []);
            }
        } catch (err) {
            toast.error('Failed to load exams');
        } finally {
            setLoading(false);
        }
    }

    async function getProfile() {
        try {
            const res = await ExamService.getUserInfoWtihoutExams();
            if (res.status === 200) {
                setProfile(res.data.data.studentInfo[0]);
            }
        } catch (e) {}
    }

    useEffect(() => {
        fetchExams();
        getProfile();
    }, []);

    async function openInstructionModal(examId: string) {
        setInstructionModal({ examId, pdf: null, loading: true });
        setInstructionAgreed(false);
        try {
            const res = await ExamService.getInstructions(examId);
            if (res.status === 200) {
                const pdf = res.data.data.instructions;
                setInstructionModal({ examId, pdf: pdf || null, loading: false });
            }
        } catch (e) {
            setInstructionModal({ examId, pdf: null, loading: false });
        }
    }

    async function handleEnroll(examId: string) {
        setEnrollingId(examId);
        try {
            const res = await ExamService.selfEnroll(examId);
            if (res.status === 200) {
                toast.success('Enrolled! Payment verification pending.');
                setInstructionModal(null);
                fetchExams();
            }
        } catch (err: any) {
            errorResponseHelper(err);
        } finally {
            setEnrollingId(null);
        }
    }

    async function generateTokenForLiveExam(examId: string, mappingId: string) {
        const res = await LiveExam.generateTokenForLiveExam(examId, mappingId);
        if (res.status === 200) {
            sessionStorage.setItem('liveToken', res.data.data.token);
            toast.success('Exam countdown has begun');
            navigate('/live/exam-live', { state: { examId, mappingId } });
        }
    }

    async function handleStart(examId: string, mappingId: string) {
        setStartingId(examId);
        try {
            const res = await ExamService.startExamId(examId);
            if (res.status === 200 && res.data) {
                if (res.data.examStart === true) {
                    await generateTokenForLiveExam(examId, mappingId);
                } else {
                    navigate('/exam/instructions', { state: { examId, mappingId } });
                }
            }
        } catch (err: any) {
            toast.error(err?.response?.data || 'Could not start exam');
        } finally {
            setStartingId(null);
        }
    }

    async function handleLogout() {
        try {
            const res = await AuthService.logout({ devInfo: await fingerPrint() });
            if (res.status === 200) {
                await Auth.signout();
                localStorage.setItem("authKey", res.data.data.token);
                toast.success("Logged out");
            }
            navigate('/login');
        } catch (err) {
            navigate('/login');
        }
    }

    async function handleViewCertificate(mappingId: string) {
        setCertificateLoading(mappingId);
        try {
            const res = await ExamService.getCertificate(mappingId);
            if (res.status === 200 && res.data?.certificate) {
                let pdfData: string = res.data.certificate;
                // Strip data URI prefix if present
                if (pdfData.includes(',')) {
                    pdfData = pdfData.split(',')[1];
                }
                const byteCharacters = atob(pdfData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            } else {
                toast.error('Certificate not available yet');
            }
        } catch (err: any) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Certificate not available');
        } finally {
            setCertificateLoading(null);
        }
    }

    // Find the first enrolled+paid+active exam as the featured one
    const featuredExam = liveExams.find(e => e.isEnrolled && e.isFeePaid && !e.completionStatus && e.mappingActive);

    const tabs = ['All', 'Enrolled', 'Available', 'Completed'];
    const filteredExams = liveExams.filter(e => {
        if (activeTab === 1) return e.isEnrolled;
        if (activeTab === 2) return !e.isEnrolled;
        if (activeTab === 3) return e.completionStatus;
        return true;
    });

    const profileName = profile ? `${profile.name || ''} ${profile.last_name || ''}`.trim() : 'User';

    return (
        <PageShell t={t} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <TopBar
                t={t}
                crumbs={['Dashboard']}
                right={<>
                    <button
                        onClick={() => navigate('/exam/user/profile')}
                        style={{ width: 32, height: 32, borderRadius: 8, background: 'transparent', border: `1px solid ${t.border}`, color: t.inkSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        {NxIcon.info(t.inkSoft)}
                    </button>
                    <UserChip t={t} name={profileName} email={profile?.email} onLogout={handleLogout} />
                </>}
            />

            <div style={{ flex: 1, overflow: 'auto', padding: '28px 40px 40px' }}>
                {/* Greeting */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
                    <div>
                        <div style={{ fontSize: 12, color: t.inkMute, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
                            {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        <h1 style={{
                            // fontFamily: '"Instrument Serif", serif',
                            fontSize: 34, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.01em',
                            color: t.ink, margin: '6px 0 0',
                        }}>
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.name || 'there'}.
                        </h1>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: 13 }}>
                        {[
                            { l: 'In progress', v: liveExams.filter(e => e.isEnrolled && !e.completionStatus).length },
                            { l: 'Available', v: liveExams.filter(e => !e.isEnrolled).length },
                            { l: 'Completed', v: liveExams.filter(e => e.completionStatus).length },
                        ].map((s, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span style={{ fontSize: 22, fontWeight: 500, color: t.ink, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{s.v}</span>
                                <span style={{ fontSize: 11, color: t.inkMute, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.l}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Featured card */}
                    <div style={{
                        background: t.surface, border: `1px solid ${t.border}`, borderRadius: 14,
                        padding: 26, position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: t.accent }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <Pill t={t} tone="live">Live now</Pill>
                        </div>
                        <div style={{
                            fontSize: 26, fontWeight: 400, lineHeight: 1.15, letterSpacing: '-0.005em', color: t.ink,
                        }}>Fetal Radiology Skill Certificate (FRSC)</div>
                    </div>

                {/* Section header + tabs */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 36, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 500, color: t.ink, margin: 0, letterSpacing: '-0.005em' }}>All live exams</h2>
                        <span style={{ fontSize: 12, color: t.inkMute }}>{liveExams.length} total</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, padding: 3, background: t.paperAlt, borderRadius: 7, fontSize: 12 }}>
                        {tabs.map((s, i) => (
                            <button
                                key={s}
                                onClick={() => setActiveTab(i)}
                                style={{
                                    padding: '4px 10px', borderRadius: 5, border: 0,
                                    background: i === activeTab ? t.surface : 'transparent',
                                    color: i === activeTab ? t.ink : t.inkSoft,
                                    fontWeight: i === activeTab ? 500 : 400,
                                    fontFamily: 'inherit', cursor: 'pointer',
                                    boxShadow: i === activeTab ? '0 1px 2px rgba(0,0,0,.04)' : 'none',
                                }}
                            >{s}</button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: t.inkMute }}>Loading...</div>
                ) : (
                    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{
                            display: 'grid', gridTemplateColumns: '1.6fr 1fr 110px 130px 130px',
                            padding: '12px 20px', gap: 16,
                            borderBottom: `1px solid ${t.border}`,
                            fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600,
                            background: t.paperAlt,
                        }}>
                            <span>Exam</span>
                            <span>Subject</span>
                            <span>Status</span>
                            <span>Payment</span>
                            <span style={{ textAlign: 'right' }}>Action</span>
                        </div>

                        {filteredExams.length === 0 ? (
                            <div style={{ padding: '24px 20px', textAlign: 'center', color: t.inkMute, fontSize: 13 }}>
                                No exams found
                            </div>
                        ) : (
                            filteredExams.map((exam: any, i: number) => {
                                const isActive = featuredExam && exam._id === featuredExam._id;
                                return (
                                    <div key={exam._id} style={{
                                        display: 'grid', gridTemplateColumns: '1.6fr 1fr 110px 130px 130px',
                                        padding: '16px 20px', gap: 16, alignItems: 'center',
                                        borderBottom: i < filteredExams.length - 1 ? `1px solid ${t.border}` : 'none',
                                        background: isActive ? withAlpha(t.accent, 0.02) : 'transparent',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                            {isActive && <span style={{ width: 6, height: 6, borderRadius: 99, background: t.accent, flexShrink: 0 }} />}
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: 13.5, color: t.ink, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {exam.examName}
                                                </div>
                                                <div style={{ fontSize: 11, color: t.inkMute, marginTop: 2 }}>
                                                    {exam.totalQuestions || '—'} questions
                                                </div>
                                            </div>
                                        </div>
                                        <span style={{ fontSize: 13, color: t.inkSoft }}>{exam.subject || '—'}</span>
                                        <span>
                                            <Pill t={t} tone={exam.isEnrolled ? (exam.completionStatus ? 'neutral' : 'enrolled') : 'notenrolled'}>
                                                {exam.completionStatus ? 'Completed' : exam.isEnrolled ? 'Enrolled' : 'Not enrolled'}
                                            </Pill>
                                        </span>
                                        <span style={{ fontSize: 12, color: !exam.isEnrolled ? t.inkSoft : exam.isFeePaid ? t.success : t.warning }}>
                                            {!exam.isEnrolled ? '—' : exam.isFeePaid ? 'Paid' : 'Pending'}
                                        </span>
                                        <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            {renderAction(exam)}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            {/* Instruction modal before enrol */}
            {instructionModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: t.surface, borderRadius: 14, width: '90%', maxWidth: 800,
                        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    }}>
                        <div style={{
                            padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Exam Instructions</h3>
                            <button
                                onClick={() => setInstructionModal(null)}
                                style={{
                                    background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                    padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >Close</button>
                        </div>

                        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', justifyContent: 'center' }}>
                            {instructionModal.loading ? (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>Loading instructions...</div>
                            ) : instructionModal.pdf ? (
                                <AllPagesPDFViewer pdf={instructionModal.pdf} width={720} />
                            ) : (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>No instructions available for this exam.</div>
                            )}
                        </div>

                        <div style={{
                            padding: '14px 24px', borderTop: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <label
                                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: t.inkSoft, cursor: 'pointer' }}
                                onClick={() => setInstructionAgreed(!instructionAgreed)}
                            >
                                <span style={{
                                    width: 16, height: 16, borderRadius: 4,
                                    background: instructionAgreed ? t.accent : 'transparent',
                                    border: instructionAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {instructionAgreed && NxIcon.check('#fff')}
                                </span>
                                I have read and understood the instructions.
                            </label>
                            <NxButton
                                t={t} kind="primary"
                                disabled={!instructionAgreed || enrollingId === instructionModal.examId}
                                onClick={() => handleEnroll(instructionModal.examId)}
                            >
                                {enrollingId === instructionModal.examId ? 'Enrolling...' : 'Proceed to Enrol'}
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}
        </PageShell>
    );

    function renderAction(exam: any) {
        if (!exam.isEnrolled) {
            return (
                <button
                    onClick={() => openInstructionModal(exam._id)}
                    style={{
                        height: 32, padding: '0 14px', borderRadius: 7,
                        border: `1px solid ${t.borderStrong}`, background: 'transparent', color: t.ink,
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}
                >
                    Enrol
                </button>
            );
        }

        if (!exam.isFeePaid) {
            return <span style={{ fontSize: 11, color: t.warning }}>Payment pending</span>;
        }

        if (exam.completionStatus) {
            return (
                <div style={{ display: 'flex', gap: 6 }}>
                    <button
                        disabled
                        style={{
                            height: 32, padding: '0 14px', borderRadius: 7,
                            border: `1px solid ${t.border}`, background: 'transparent', color: t.inkMute,
                            fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'not-allowed',
                        }}
                    >
                        Completed
                    </button>
                    {exam.mappingId && (
                        <button
                            onClick={() => handleViewCertificate(exam.mappingId)}
                            disabled={certificateLoading === exam.mappingId}
                            style={{
                                height: 32, padding: '0 14px', borderRadius: 7,
                                border: `1px solid ${t.accent}`, background: 'transparent', color: t.accent,
                                fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                opacity: certificateLoading === exam.mappingId ? 0.5 : 1,
                            }}
                        >
                            {certificateLoading === exam.mappingId ? 'Loading...' : 'Certificate'}
                        </button>
                    )}
                </div>
            );
        }

        if (!exam.mappingActive) {
            return <span style={{ fontSize: 11, color: t.warning }}>Not allowed</span>;
        }

        return (
            <button
                onClick={() => handleStart(exam._id, exam.mappingId)}
                disabled={startingId === exam._id}
                style={{
                    height: 32, padding: '0 14px', borderRadius: 7,
                    border: 'none', background: t.ink, color: t.paper,
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    opacity: startingId === exam._id ? 0.5 : 1,
                }}
            >
                {startingId === exam._id ? 'Starting...' : <>Continue {NxIcon.arrowRight(t.paper)}</>}
            </button>
        );
    }
}
